import React, { useState } from 'react';
import { render } from 'react-dom';
import { string, object } from 'prop-types';
import { Provider, connect } from 'react-redux';
import { Router, browserHistory, Route } from 'react-router';
import BpClientPublicApp from './bpClientPublicApp.js';
import bpClientLogger from './common/helpers/bpClientLogger.js';
import * as apiHelper from './common/helpers/apiHelper.js';
import ProgressIndicator from './components/progressIndicator/component.js';
import store from './common/store.js';
import ContactUs from './contactus/V2/ContactUs.js';
import * as HomeActions from './home/actions.js';
import gateWayController from './gateWay/gateWayController.js';
import { FFContextProvider } from '@harnessio/ff-react-client-sdk';
import { getFeatureFlagTarget } from './common/helpers/ldUtils';
import withFlags from './components/WithFeatureFlags/component';
import { errorToString } from './common/helpers/errorToString';

const ErrorComponent = message => (
  <div className="messagePanelContainer">
    <div className={'bp-alert bp-dismissable on bp-negative'}>
      <div className="media">
        <div className="media-left media-middle">
          <span className={'fa fa-exclamation'}></span>
        </div>
        <div className="media-body media-middle">
          <p>{message}</p>
        </div>
      </div>
    </div>
  </div>
);

const UnknownRoute = () => ErrorComponent(`Page '${browserHistory.getCurrentLocation().pathname}' not found!`);
const PageFailed = () => (
  <ErrorComponent message="We ran into a problem loading Producer Toolbox. Please try again or contact User Software Support (USS) at 888-268-4361." />
);

const CoreApp = ({ flags }) => {
  const path = window.location.pathname.split('/apps/ptb/').pop();

  return (
    <Provider store={store}>
      <Router history={browserHistory} onUpdate={() => window.scrollTo(0, 0)}>
        <Route path="/apps/ptb" component={BpClientPublicApp} design={1}>
          <Route path="public/contactus" flags={flags} component={ContactUs}></Route>
          {flags?.enableSsoUrlRedirection && <Route path="gateway/cii" component={gateWayController}></Route>}
          {path && !path.includes('gateway/') && <Route path="*" component={UnknownRoute} />}
        </Route>
      </Router>
    </Provider>
  );
};

CoreApp.propTypes = {
  flags: object,
};

const WrappedCoreApp = withFlags(CoreApp);

const HarnessApp = props => {
  const [harnessError, setHarnessError] = useState(null);

  if (harnessError) {
    return (
      <ErrorComponent message="Failed to initialize the application. Please try after sometime or contact User Software Support (USS) at 888-268-4361." />
    );
  }

  return (
    <FFContextProvider
      onError={error => {
        console.error(error);
        bpClientLogger.error(`Failed to initialize Harness Client. Please find the error=${errorToString(error)}`);
        setHarnessError(error);
      }}
      fallback={<ProgressIndicator colorType="white" message="Loading, please wait" />}
      apiKey={props.clientSideId}
      target={getFeatureFlagTarget(props)}
    >
      <WrappedCoreApp />
    </FFContextProvider>
  );
};

HarnessApp.propTypes = {
  clientSideId: string,
  userId: string,
  encryptedTaxId: string,
  emailAddress: string,
  tokenizedUserId: string,
  tokenizedEncryptedTaxId: string,
  tokenizedEmailAddress: string,
};

const mapStateToProps = state => {
  const userProfile = state?.homeReducers?.bpUserInfo?.profileObj || {};
  return {
    userId: userProfile?.profile?.userId,
    emailAddress: userProfile?.profile?.producer?.emailAddress,
    encryptedTaxId: userProfile?.profile?.producer?.encryptedTaxId,
    tokenizedUserId: userProfile?.tokenized?.userId,
    tokenizedEncryptedTaxId: userProfile?.tokenized?.encryptedTaxId,
    tokenizedEmailAddress: userProfile?.tokenized?.emailAddress,
  };
};

const HarnessMainApp = connect(mapStateToProps)(HarnessApp);

async function renderApp() {
  let config = {};
  try {
    render(
      <div>
        <ProgressIndicator colorType="white" message="Loading, please wait" />
      </div>,
      document.getElementById('app')
    );
    const path = window.location.pathname.split('/apps/ptb/').pop();
    const isPTBConfigLoaded = path.includes('gateway/');
    config = isPTBConfigLoaded ? await apiHelper.getClientConfig() : await apiHelper.getPublicClientConfig();
    const harnessClientKey = path.includes('gateway/') ? config?.clientHarnessSDKKey : config?.harnessSDKKey;
    store.dispatch(HomeActions.updateComponentState('HOME_CLIENT_CONFIG', { config: { data: config } }));
    render(
      <Provider store={store}>
        <HarnessMainApp clientSideId={harnessClientKey} />
      </Provider>,
      document.getElementById('app')
    );
  } catch (error) {
    console.error('Failed to initialize the application', error);
    bpClientLogger.error(`Failed to initialize the application, error=${errorToString(error)}`);
    render(<PageFailed />, document.getElementById('app'));
  }
}

renderApp();
